// File: src/utils.js
// Utility functions for points calculation

// Parse a MyRCM ranking report document into raw result rows.
//
// MyRCM v9 (redesigned 2026) renders both the "Ranking Finals" and
// "Ranking Qualifying" reports with the same table layout. The columns are not
// stable, though: as of 2026-08 MyRCM has commented the "No." column out of
// both the header and the body,
//
//   <th>R</th>  <!--<th>No.</th>-->  <th>&nbsp;</th>  <th>Driver</th>  <th>L</th>
//
// which shifted every following column one to the left. Reading fixed indices
// meant the lap count was imported as the driver name, so columns are located
// by the data-label each cell carries (data-label="Driver", data-label="No."),
// falling back to the header text and only then to the historical positions.
//
// The results table used to carry id="data-table"; it is now
// <table class="... ranking-table">. Between data rows the page also
// injects collapsible per-driver heat-detail rows
// (<tr id="ranking-details-N" class="ranking-heat-details-row" hidden>)
// which must be skipped.
//
// Returns null when no ranking table is present (so callers can surface a
// "table not found" error), otherwise an array of
// { rank, carNumber, driverName } for each driver row.

const RANKING_COLUMN_PATTERNS = {
  // MyRCM is requested in English, but parts of its UI leak other languages.
  rank: /^(r|rank|rang|pos\.?|position)$/i,
  carNumber: /^(no\.?|nr\.?|num\.?|#)$/i,
  driver: /^(driver|fahrer|pilote|pilota|kuljettaja|f.rare)$/i
};

const normaliseCellText = (value) =>
  (value || '')
    .replace(/\u00a0/g, ' ') // NBSP -> space
    .replace(/\s+/g, ' ')
    .trim();

// Locate the rank / car number / driver columns for a ranking table, preferring
// the semantic anchors over positions that MyRCM keeps moving.
const findRankingColumns = (table, cells) => {
  const columns = { rank: -1, carNumber: -1, driver: -1 };

  // 1. data-label on the body cells themselves.
  cells.forEach((cell, index) => {
    const label = normaliseCellText(cell.getAttribute('data-label'));
    if (!label) return;
    Object.keys(columns).forEach((key) => {
      if (columns[key] === -1 && RANKING_COLUMN_PATTERNS[key].test(label)) {
        columns[key] = index;
      }
    });
  });

  // 2. Header text, for tables whose cells carry no data-label.
  const headers = Array.from(table.querySelectorAll('thead th'));
  if (headers.length === cells.length) {
    headers.forEach((header, index) => {
      const label = normaliseCellText(header.textContent);
      if (!label) return;
      Object.keys(columns).forEach((key) => {
        if (columns[key] === -1 && RANKING_COLUMN_PATTERNS[key].test(label)) {
          columns[key] = index;
        }
      });
    });
  }

  // 3. The historical layout, Rank | No. | Country | Driver | Club.
  if (columns.rank === -1) columns.rank = 0;
  if (columns.driver === -1) columns.driver = cells.length > 3 ? 3 : cells.length - 1;

  return columns;
};

export const parseMyRCMRankingRows = (doc) => {
  const table = doc.querySelector('.ranking-table, #data-table');
  if (!table) return null;

  const tbody = table.querySelector('tbody');
  if (!tbody) return [];

  const rows = Array.from(tbody.children).filter((row) => row.tagName === 'TR');
  const parsed = [];

  for (const row of rows) {
    // Skip the collapsible heat-detail rows (they carry an id / helper class)
    // and any spanning header/separator rows.
    if (row.id || row.classList.contains('ranking-heat-details-row')) continue;

    const cells = Array.from(row.querySelectorAll('td'));
    if (cells[0] && cells[0].hasAttribute('colspan')) continue;
    if (cells.length < 3) continue;

    const columns = findRankingColumns(table, cells);

    const rank = parseInt(normaliseCellText(cells[columns.rank].textContent), 10);

    // The car number column is currently absent from MyRCM's markup; when it is
    // missing the importer falls back to matching drivers by name.
    const carText = columns.carNumber === -1
      ? ''
      : normaliseCellText(cells[columns.carNumber].textContent);
    const carNumber = /^\d+$/.test(carText) ? parseInt(carText, 10) : null;

    const driverName = normaliseCellText(cells[columns.driver].textContent);

    // A driver name that is purely numeric means we located the wrong column;
    // drop the row rather than importing a lap count as a person.
    if (isNaN(rank) || !driverName || /^\d+$/.test(driverName)) continue;

    parsed.push({ rank, carNumber, driverName });
  }

  return parsed;
};

// ---------------------------------------------------------------------------
// MyRCM event / class browsing
//
// These let the import dialog show MyRCM's own event list in-app instead of
// making the user open myrcm.ch, navigate to the event and class, and copy the
// address bar. They only produce the same base URL that used to be pasted:
//
//   https://www.myrcm.ch/en/report/<eventId>/<classId>
//
// MyRCM renders its list filters as a POST form, but the same filters are
// honoured as plain GET query parameters, which is what makes this possible
// without a headless browser.
// ---------------------------------------------------------------------------

export const MYRCM_ORIGIN = 'https://www.myrcm.ch';

// MyRCM shows 25 events per page and does not let you change it.
export const MYRCM_PAGE_SIZE = 25;

// The two event lists worth browsing: finished events (which have results) and
// events running right now. "Upcoming" is deliberately excluded - it has
// nothing to import.
export const MYRCM_SCOPES = ['archive', 'live'];

// MyRCM's internal country ids, keyed by the ISO codes used in data/countries.
//
// Only used to PRESELECT the filter when the browser opens. The dropdown itself
// is rebuilt from the country list on the fetched page, so every country MyRCM
// offers stays reachable whether or not it is here.
//
// These are MyRCM's own numbers and cannot be derived from anything - they were
// read off their archive page, which is the same list parseMyRCMCountryOptions
// scrapes at runtime. All 61 countries MyRCM had are here; their 62nd entry is
// "DEMO", which is not a country.
//
// A country MyRCM does not have simply has no entry and falls back to "all
// countries", which is the honest answer rather than a wrong preselection.
// Estonia is the one that will surprise a Finnish organizer: MyRCM has no
// Estonia at all, so EE is absent on purpose.
//
// Matching by NAME at runtime is not safe and is why this table exists: MyRCM
// says "United States of America", "Czech Republic", "Korea (South)" and
// "Bonaire, Caribisch Nederland" where the ISO list says something else.
export const MYRCM_COUNTRY_IDS = {
  AR: 31, AT: 17, AU: 34, BA: 45, BE: 8, BG: 51, BQ: 248, BR: 48,
  BY: 40, CA: 57, CH: 2, CL: 62, CN: 63, CW: 245, CY: 73, CZ: 6,
  DE: 3, DK: 10, DO: 76, ES: 199, FI: 88, FR: 11, GB: 222, GR: 7,
  HK: 108, HR: 16, HU: 109, ID: 112, IT: 12, JP: 118, KR: 124, LI: 133,
  LT: 134, LU: 5, LV: 128, MC: 151, MO: 135, MQ: 144, MX: 148, MY: 139,
  NL: 9, NO: 14, NZ: 162, PE: 175, PH: 176, PL: 178, PT: 15, RE: 181,
  RO: 22, RU: 13, SE: 4, SG: 194, SI: 18, SK: 19, TH: 209, TR: 215,
  TW: 206, UA: 220, US: 21, UY: 224, VE: 228
};

// Build a MyRCM event-list URL. `scope` is 'archive' or 'live'; `countryId` is
// a MyRCM country id (falsy = all countries); `page` is 0-based.
export const buildMyRCMListUrl = ({ scope = 'archive', countryId, search, page = 0 } = {}) => {
  const listScope = MYRCM_SCOPES.includes(scope) ? scope : 'archive';
  const params = new URLSearchParams();

  if (countryId) params.set('country', String(countryId));
  if (search && search.trim()) params.set('dFi', search.trim());
  if (page > 0) params.set('pId', String(page));

  const query = params.toString();
  return `${MYRCM_ORIGIN}/en/${listScope}${query ? `?${query}` : ''}`;
};

// The event's report page, which carries the list of classes.
export const buildMyRCMEventReportUrl = (eventId) =>
  `${MYRCM_ORIGIN}/en/report/${eventId}`;

// The per-class base URL handed to the existing import flow, which appends its
// own reportKey. Must be the canonical /en/... form: the backend proxy does not
// follow redirects, so the legacy /myrcm/report/en/... shape would come back as
// a redirect body.
export const buildMyRCMClassUrl = (eventId, classId) =>
  `${MYRCM_ORIGIN}/en/report/${eventId}/${classId}`;

// Read an integer out of a single element, e.g. the result counters.
const readIntBySelector = (doc, selector) => {
  const el = doc.querySelector(selector);
  if (!el) return null;
  const value = parseInt(el.textContent.replace(/\D/g, ''), 10);
  return isNaN(value) ? null : value;
};

const cleanText = (value) =>
  (value || '')
    .replace(/ /g, ' ') // NBSP -> space
    .replace(/\s+/g, ' ')
    .trim();

// Assemble the day/month/year spans of one .date-box into a display string.
// The month is a localised abbreviation, so there is nothing to gain from
// parsing this into a real Date - it is only ever shown.
const parseDateBox = (box) => {
  if (!box) return '';
  const day = cleanText(box.querySelector('.date-day')?.textContent);
  const month = cleanText(box.querySelector('.date-month')?.textContent);
  const year = cleanText(box.querySelector('.date-year')?.textContent);
  return [day, month, year].filter(Boolean).join(' ');
};

// Read one .event-meta span, dropping its bold label. The labels are read
// positionally rather than by text because MyRCM's markup is only partly
// translated (German labels leak through in places), so the label is not a
// reliable key.
const parseMetaValue = (spans, index) => {
  const span = spans[index];
  if (!span) return '';
  const label = span.querySelector('strong');
  const labelText = label ? label.textContent : '';
  return cleanText(span.textContent.replace(labelText, ''));
};

// Parse a MyRCM event-list page (/en/archive, /en/live) into event rows.
//
// Returns null when the list section is missing entirely - that means the page
// did not look the way we expect (markup drift, or an error page came back
// through the proxy), and the caller should say so rather than show an empty
// list. An empty `events` array with a total of 0 is a legitimate "no matches".
export const parseMyRCMEventList = (doc) => {
  const list = doc.querySelector('section.event-list');
  if (!list) return null;

  const cards = Array.from(list.querySelectorAll('article.event-card'));

  const events = cards.map((card) => {
    // The title link and the "Details" button point at the same URL; read only
    // the title so there is nothing to dedupe afterwards.
    const link = card.querySelector('.event-title a[href]');
    const href = link ? link.getAttribute('href') : '';
    const idMatch = href.match(/^\/en\/(?:archive|live|upcoming)\/(\d+)$/);
    if (!idMatch) return null;

    const dateBoxes = card.querySelectorAll('.event-dates .date-box');
    const metaSpans = card.querySelectorAll('.event-meta > span');

    return {
      id: idMatch[1],
      name: cleanText(link.textContent),
      dateFrom: parseDateBox(dateBoxes[0]),
      dateTo: dateBoxes.length > 1 ? parseDateBox(dateBoxes[1]) : '',
      organizer: parseMetaValue(metaSpans, 0),
      location: parseMetaValue(metaSpans, 1),
      country: parseMetaValue(metaSpans, 2),
      countryCode: cleanText(card.querySelector('.event-badges .badge.country')?.textContent),
      // Without this badge the event has no published reports, so there is
      // nothing to import from it.
      hasReports: !!card.querySelector('.event-badges .badge.report')
    };
  }).filter(Boolean);

  // MyRCM's own counters, which honour the active filters. The pagination links
  // carry their page number only inside an onclick attribute, so these are the
  // only usable source for "is there a next page".
  const total = readIntBySelector(doc, '#eventResultTotal');

  return {
    events,
    total: total === null ? events.length : total,
    from: readIntBySelector(doc, '#eventResultFrom'),
    to: readIntBySelector(doc, '#eventResultTo')
  };
};

// Parse the class dropdown on an event's report page (/en/report/<eventId>).
//
// Returns null when the select is missing (unexpected page), and an empty array
// when it is present but empty - which is what MyRCM serves for an event id
// that has no published classes.
export const parseMyRCMClassList = (doc) => {
  const select = doc.querySelector('#reportClass');
  if (!select) return null;

  return Array.from(select.querySelectorAll('option'))
    .map((option) => ({
      id: option.getAttribute('value'),
      name: cleanText(option.textContent)
    }))
    .filter((item) => item.id && item.name);
};

// Parse the country filter checkboxes that every event-list page carries, so
// the dropdown always matches what MyRCM currently offers. Returns an empty
// array when the panel is absent - the country filter simply degrades to "all
// countries" rather than breaking the browser.
export const parseMyRCMCountryOptions = (doc) => {
  const inputs = doc.querySelectorAll('input[type="checkbox"][name="country"]');

  return Array.from(inputs)
    .map((input) => ({
      id: input.getAttribute('value'),
      name: cleanText(input.closest('label')?.querySelector('span')?.textContent)
    }))
    .filter((item) => item.id && item.name);
};

export const calculatePointsForPosition = (position) => {
  const pointsTable = {
    1: 100, 2: 90, 3: 82, 4: 76, 5: 72, 6: 69, 7: 66, 8: 63,
    9: 60, 10: 57, 11: 54, 12: 52, 13: 50, 14: 48, 15: 46,
    16: 44, 17: 42, 18: 40, 19: 38, 20: 36, 21: 34, 22: 32,
    23: 30, 24: 28, 25: 26, 26: 24, 27: 22, 28: 21, 29: 20,
    30: 19, 31: 18, 32: 17, 33: 16, 34: 15, 35: 14, 36: 13,
    37: 12, 38: 11, 39: 10, 40: 9, 41: 8, 42: 7, 43: 6, 44: 5,
    45: 4, 46: 3, 47: 2, 48: 1, 49: 1, 50: 1
  };

  // Return points from table if position exists, otherwise return 1 point for positions beyond 50
  return pointsTable[position] || 1;
};

export const calculateQualificationPoints = (position) => {
  const qualPoints = {
    1: 5,
    2: 4,
    3: 3,
    4: 2,
    5: 1
  };
  return qualPoints[position] || 0;
};

// Format series years for display
// Examples: "2024", "2024-2025"
export const formatSeriesYear = (startYear, endYear) => {
  if (!startYear) return '';
  if (!endYear || endYear === startYear) return `${startYear}`;
  return `${startYear}-${endYear}`;
};

// Format series name with year
export const formatSeriesNameWithYear = (name, startYear, endYear) => {
  const year = formatSeriesYear(startYear, endYear);
  return year ? `${name} (${year})` : name;
};

// Check if a series is active in a given year
export const isSeriesActiveInYear = (series, year) => {
  // If series has no start_year (legacy), show it in all years
  if (!series.start_year) return true;

  // Series is active if:
  // - start_year <= year AND (no end_year OR end_year >= year)
  const startYear = series.start_year;
  const endYear = series.end_year || startYear; // If no end year, use start year

  return year >= startYear && year <= endYear;
};

// Get all unique years from series list
export const getAvailableYears = (seriesList) => {
  const years = new Set();

  seriesList.forEach(series => {
    if (series.start_year) {
      years.add(series.start_year);
      if (series.end_year && series.end_year !== series.start_year) {
        // Add all years in range for multi-year series
        for (let year = series.start_year; year <= series.end_year; year++) {
          years.add(year);
        }
      }
    }
  });

  // If no years found, add current year
  if (years.size === 0) {
    years.add(new Date().getFullYear());
  }

  return Array.from(years).sort((a, b) => b - a); // Sort descending (newest first)
};

// Get current year
export const getCurrentYear = () => new Date().getFullYear();

// ===== DATE FORMATTING UTILITIES =====
// Standardized date handling for consistent display and storage

/**
 * Format a date for display using the browser's locale
 * This ensures consistent, localized date formatting across the app
 * @param {string|Date} date - Date to format (ISO string, Date object, or timestamp)
 * @returns {string} Formatted date string in browser's locale (e.g., "12/31/2024" or "31/12/2024")
 */
export const formatDateForDisplay = (date) => {
  if (!date) return '';

  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;

    // Check for invalid date
    if (isNaN(dateObj.getTime())) return '';

    // Use browser's locale with consistent options
    return dateObj.toLocaleDateString(undefined, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
};

/**
 * Format a date for database storage (ISO format: yyyy-mm-dd)
 * @param {string|Date} date - Date to format
 * @returns {string} ISO date string (e.g., "2024-12-31")
 */
export const formatDateForDatabase = (date) => {
  if (!date) return '';

  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;

    // Check for invalid date
    if (isNaN(dateObj.getTime())) return '';

    // Return ISO format (yyyy-mm-dd)
    return dateObj.toISOString().split('T')[0];
  } catch (error) {
    console.error('Error formatting date for database:', error);
    return '';
  }
};

/**
 * Format a datetime for display using the browser's locale
 * Includes both date and time
 * @param {string|Date} datetime - DateTime to format
 * @returns {string} Formatted datetime string in browser's locale
 */
export const formatDateTimeForDisplay = (datetime) => {
  if (!datetime) return '';

  try {
    const dateObj = typeof datetime === 'string' ? new Date(datetime) : datetime;

    // Check for invalid date
    if (isNaN(dateObj.getTime())) return '';

    // Use browser's locale with date and time
    return dateObj.toLocaleString(undefined, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (error) {
    console.error('Error formatting datetime:', error);
    return '';
  }
};

/**
 * Format a Date as yyyy-mm-dd using its LOCAL calendar date.
 *
 * `toISOString()` converts to UTC first, so for a user east of Greenwich a date
 * built from local components can come back as the previous day. Anything that
 * compares against a date-only value from the API must use this instead.
 *
 * @param {Date} date
 * @returns {string} e.g. "2026-04-18"
 */
export const toLocalISODate = (date) => {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

/**
 * Convert a UTC datetime from the API into the value a <input type="datetime-local">
 * expects, which is always LOCAL wall-clock time.
 *
 * Entry windows are stored and compared in UTC on the server. Showing the stored
 * value directly would tell a Finnish organizer their entry opens at 13:28 when
 * the server will actually open it at 16:28.
 *
 * @param {string} utc - "2026-08-28T13:28" or a full ISO string, in UTC
 * @returns {string} "2026-08-28T16:28" in the viewer's timezone, or ''
 */
export const utcToLocalInput = (utc) => {
  if (!utc) return '';

  // Check the shape first. Appending a timezone to arbitrary text can produce a
  // string Date happens to parse into a real - and completely wrong - moment.
  if (!/^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(utc)) return '';

  // The API sends "YYYY-MM-DDTHH:mm"; mark it as UTC so it is not read as local.
  const withZone = /[Zz]|[+-]\d{2}:\d{2}$/.test(utc) ? utc : `${utc.replace(' ', 'T').slice(0, 16)}:00Z`;
  const date = new Date(withZone);
  if (isNaN(date.getTime())) return '';

  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
         `T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

/**
 * The inverse: take what the organizer typed, in their own timezone, and produce
 * the UTC value the server stores and compares.
 *
 * @param {string} local - "2026-08-28T16:28" as entered
 * @returns {string|null} "2026-08-28 13:28:00" in UTC, or null
 */
export const localInputToUtc = (local) => {
  if (!local) return null;

  if (!/^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(local)) return null;

  // A date-time with no offset is parsed as local time, which is what we want.
  const date = new Date(local);
  if (isNaN(date.getTime())) return null;

  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())} ` +
         `${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}:00`;
};

/**
 * Get today's date in ISO format (yyyy-mm-dd) for date inputs
 * @returns {string} Today's date in ISO format
 */
export const getTodayISO = () => {
  return new Date().toISOString().split('T')[0];
};